import { ChangeEvent, useRef } from 'react'

import { Image, Member } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useUpdateMember,
  useImageUploader,
} from '@tribeplatform/react-sdk/hooks'
import { Avatar, AvatarSize } from '@tribeplatform/react-ui-kit/Avatar'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { useThemeContext } from '@tribeplatform/react-ui-kit/ThemeProvider'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useCropImageModal } from '../../../CropImageModal/useCropImageModal.js'
import { T } from '../../../i18n/components/T.js'
import { useI18n } from '../../../i18n/providers/I18nProvider.js'
import { handleUploadFileError } from '../../../utils/error.js'
import { MemberAvatarPlaceholder } from './MemberAvatarPlaceholder.js'

interface Props {
  member: Member
  className?: string
  size?: AvatarSize
  editable?: boolean
}
export const ProfilePicture = ({
  member,
  className,
  size = '8x',
  editable = true,
}: Props) => {
  const intl = useI18n()
  const { $t } = intl
  const { Link } = useRouter()
  const { style } = useThemeContext()

  const fileInputRef = useRef<HTMLInputElement>()
  const { mutateAsync: updateMember, isLoading: updateMemberLoading } =
    useUpdateMember()
  const {
    uploadImage,
    isLoading: isUploadingImages,
    loadingImages,
  } = useImageUploader()

  const onUpload = async ({ croppedImage }) => {
    try {
      const uploadedImage = await uploadImage(croppedImage)
      await updateMember({
        input: {
          profilePictureId: uploadedImage.id,
        },
        id: member.id,
      })
      toast({
        title: $t({
          id: 'ProfilePicture.Updated',
          defaultMessage: 'Profile picture successfully updated',
        }),
        status: 'success',
      })
    } catch (error) {
      handleUploadFileError(intl)(error)
    }
  }

  const { onSelectFile, children: cropModal } = useCropImageModal({
    onSuccess: onUpload,
    options: {
      shape: style.avatarShape === 'full-rounded' ? 'round' : 'rect',
      format: 'image/jpeg',
    },
  })

  const onChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(e.target.files)?.[0] as File
    // allow to select same file twice
    e.target.value = ''
    onSelectFile(file)
  }

  const avatar = loadingImages.at(0) || (member.profilePicture as Image)
  const avatarMarkup = (
    <Avatar
      size={size}
      name={member.name}
      className={className}
      src={avatar?.urls?.small || avatar?.url}
      fallback={MemberAvatarPlaceholder}
    />
  )
  return (
    <div>
      <div className="hover:bg-surface-hovered rounded-avatar relative">
        {editable ? (
          avatarMarkup
        ) : (
          <Link href={member.relativeUrl} disabled={editable}>
            {avatarMarkup}
          </Link>
        )}
        {editable && (
          <input
            type="file"
            disabled={updateMemberLoading}
            className="hidden"
            multiple={false}
            ref={fileInputRef}
            onChange={onChangeFile}
            accept="image/*"
          />
        )}
        {(isUploadingImages || updateMemberLoading) && (
          <div className="flex items-center justify-center text-white rounded-avatar bg-background-backdrop absolute top-0 bottom-0 start-0 end-0">
            <SvgIcon className="animate-spin h-7 w-7" name="spinner" />
          </div>
        )}
        {editable && !(isUploadingImages || updateMemberLoading) && (
          <div
            onClick={() => {
              fileInputRef.current?.click()
            }}
            className="flex items-center justify-center rounded-avatar text-white bg-black bg-opacity-50 opacity-0 hover:opacity-90 absolute top-0 bottom-0 start-0 end-0 cursor-pointer"
          >
            <T id="Generics.Change.Verb" defaultMessage="Change" />
          </div>
        )}
      </div>
      {cropModal}
    </div>
  )
}
